/**
 * Система кэширования entitlements (подписка и доступ)
 * Решает проблему фликера и задержек при проверке подписки
 */

import { PaywallCheckResult } from './paywall';

// Ключи для localStorage
const ENTITLEMENTS_CACHE_KEY = 'bastion_entitlements_cache';
const ENTITLEMENTS_CACHE_TIMESTAMP_KEY = 'bastion_entitlements_cache_timestamp';
const ENTITLEMENTS_CACHE_TTL = 30 * 60 * 1000; // 30 минут TTL

// Долгоживущий флаг "был премиум" - используется как fallback при проблемах с проверкой
const PREMIUM_EVER_KEY = 'bastion_premium_ever';
const PREMIUM_EVER_TIMESTAMP_KEY = 'bastion_premium_ever_timestamp';
const PREMIUM_EVER_TTL = 7 * 24 * 60 * 60 * 1000; // 7 дней TTL для premium_ever флага

// BroadcastChannel для синхронизации между вкладками
const ENTITLEMENTS_CHANNEL_NAME = 'bastion_entitlements_sync';
let entitlementsChannel: BroadcastChannel | null = null;
if (typeof window !== 'undefined') {
  try {
    entitlementsChannel = new BroadcastChannel(ENTITLEMENTS_CHANNEL_NAME);
  } catch (e) {
    console.warn('BroadcastChannel not supported for entitlements, falling back to localStorage');
  }
}

export interface EntitlementsCache {
  userId: string | null;
  isPaid: boolean;
  isActive: boolean;
  isTrialing: boolean;
  planName?: string;
  canAccessPortfolios: boolean;
  canAccessRadar: boolean;
  timestamp: number;
}

export type EntitlementsStatus = 'unknown' | 'free' | 'paid';

// Защита от одновременных запросов (inflight requests)
let inflightRequest: Promise<PaywallCheckResult> | null = null;
let inflightRequestTimestamp = 0;
let inflightRequestUserId: string | null = null;
const INFLIGHT_TTL = 10000; // 10 секунд - если запрос висит дольше, считаем его устаревшим

// Глобальный флаг для предотвращения одновременных запросов на всех вкладках
const GLOBAL_ENTITLEMENTS_CHECK_KEY = 'bastion_global_entitlements_check';
const GLOBAL_ENTITLEMENTS_CHECK_TTL = 3000; // 3 секунды

/**
 * Читает долгоживущий флаг "был премиум" из localStorage
 */
export function readPremiumEverFlag(): { userId: string; wasPremium: boolean; timestamp: number } | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const data = localStorage.getItem(PREMIUM_EVER_KEY);
    const timestamp = localStorage.getItem(PREMIUM_EVER_TIMESTAMP_KEY);
    
    if (data && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < PREMIUM_EVER_TTL) {
        const parsed = JSON.parse(data);
        return {
          userId: parsed.userId,
          wasPremium: parsed.wasPremium,
          timestamp: parseInt(timestamp),
        };
      }
    }
  } catch (e) {
    console.warn('Error reading premium_ever flag:', e);
  }
  
  return null;
}

/**
 * Записывает долгоживущий флаг "был премиум" в localStorage
 * Записывается ТОЛЬКО при подтверждении premium статуса от API
 */
export function writePremiumEverFlag(userId: string, wasPremium: boolean): void {
  if (typeof window === 'undefined') return;
  
  try {
    // Записываем только если wasPremium = true ИЛИ если нужно сбросить (при logout)
    // При wasPremium = false и существующем флаге - НЕ перезаписываем
    if (!wasPremium) {
      // Проверяем, есть ли уже флаг для этого пользователя
      const existing = readPremiumEverFlag();
      if (existing && existing.userId === userId && existing.wasPremium) {
        // Не перезаписываем premium флаг на false - это может быть временная ошибка
        console.log('[Entitlements] Skipping premium_ever downgrade for user', userId);
        return;
      }
    }
    
    localStorage.setItem(PREMIUM_EVER_KEY, JSON.stringify({ userId, wasPremium }));
    localStorage.setItem(PREMIUM_EVER_TIMESTAMP_KEY, Date.now().toString());
  } catch (e) {
    console.warn('Error writing premium_ever flag:', e);
  }
}

/**
 * Очищает флаг "был премиум" - вызывается только при явном logout
 */
export function clearPremiumEverFlag(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(PREMIUM_EVER_KEY);
    localStorage.removeItem(PREMIUM_EVER_TIMESTAMP_KEY);
  } catch (e) {
    console.warn('Error clearing premium_ever flag:', e);
  }
}

/**
 * Читает кэш entitlements из localStorage
 */
export function readEntitlementsCache(): EntitlementsCache | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const cached = localStorage.getItem(ENTITLEMENTS_CACHE_KEY);
    const timestamp = localStorage.getItem(ENTITLEMENTS_CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      const data = JSON.parse(cached) as EntitlementsCache;
      const age = Date.now() - parseInt(timestamp);
      
      if (age < ENTITLEMENTS_CACHE_TTL) {
        return {
          ...data,
          timestamp: parseInt(timestamp),
        };
      }
    }
  } catch (e) {
    console.warn('Error reading entitlements cache:', e);
  }
  
  return null;
}

/**
 * Читает кэш entitlements БЕЗ проверки TTL.
 * Используется ТОЛЬКО как fallback при ошибках API: если сеть/Stripe тормозят
 * или вернули 5xx, и обычный кэш уже истёк, мы предпочитаем показать пользователю
 * последнее известное состояние, а не ложно сбросить его в "free" и показать пейвол.
 *
 * Не использовать для обычного чтения — может вернуть очень старые данные
 * (например, годовой давности при редких визитах).
 */
export function readEntitlementsCacheStale(): EntitlementsCache | null {
  if (typeof window === 'undefined') return null;

  try {
    const cached = localStorage.getItem(ENTITLEMENTS_CACHE_KEY);
    const timestamp = localStorage.getItem(ENTITLEMENTS_CACHE_TIMESTAMP_KEY);

    if (cached && timestamp) {
      const data = JSON.parse(cached) as EntitlementsCache;
      return {
        ...data,
        timestamp: parseInt(timestamp),
      };
    }
  } catch (e) {
    console.warn('Error reading stale entitlements cache:', e);
  }

  return null;
}

/**
 * Сохраняет entitlements в кэш
 */
export function writeEntitlementsCache(
  userId: string | null,
  result: PaywallCheckResult
): void {
  if (typeof window === 'undefined') return;
  
  try {
    const cache: EntitlementsCache = {
      userId,
      isPaid: result.isPaid,
      isActive: result.isActive,
      isTrialing: result.isTrialing,
      planName: result.planName,
      canAccessPortfolios: result.canAccessPortfolios || false,
      canAccessRadar: result.canAccessRadar || false,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(ENTITLEMENTS_CACHE_KEY, JSON.stringify(cache));
    localStorage.setItem(ENTITLEMENTS_CACHE_TIMESTAMP_KEY, Date.now().toString());
    
    // Обновляем долгоживущий флаг "был премиум"
    // ВАЖНО: При isPaid = false нужно сбросить флаг, чтобы не показывать "оптимистичный" премиум
    // когда подписка реально закончилась (past_due, canceled и т.д.)
    if (userId) {
      if (result.isPaid) {
        writePremiumEverFlag(userId, true);
      } else {
        // Принудительно сбрасываем флаг при подтверждённом отсутствии подписки
        clearPremiumEverFlag();
        console.log('[Entitlements] Cleared premium_ever flag due to isPaid=false');
      }
    }
    
    // Отправляем сообщение другим вкладкам через BroadcastChannel
    if (entitlementsChannel) {
      entitlementsChannel.postMessage({
        type: 'ENTITLEMENTS_UPDATE',
        cache,
        timestamp: Date.now(),
      });
    }
  } catch (e) {
    console.warn('Error writing entitlements cache:', e);
  }
}

/**
 * Очищает кэш entitlements (вызывается при logout)
 */
export function clearEntitlementsCache(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(ENTITLEMENTS_CACHE_KEY);
    localStorage.removeItem(ENTITLEMENTS_CACHE_TIMESTAMP_KEY);
    
    // Очищаем также долгоживущий флаг "был премиум" при logout
    clearPremiumEverFlag();
    
    // Отправляем сообщение другим вкладкам
    if (entitlementsChannel) {
      entitlementsChannel.postMessage({
        type: 'ENTITLEMENTS_CLEAR',
        timestamp: Date.now(),
      });
    }
  } catch (e) {
    console.warn('Error clearing entitlements cache:', e);
  }
}

/**
 * Восстанавливает paid-статус из локальных следов, когда API недоступен.
 * Порядок:
 *   1. Stale-кэш (TTL истёк, но данные были валидны при последнем успехе) для того же userId
 *   2. Долгоживущий флаг `premium_ever` (TTL 7 дней)
 *
 * Возвращает null, если ни одного признака paid-статуса нет — тогда вызывающий
 * код решает, показать ли free.
 */
function resolvePaidFallback(userId: string | null): PaywallCheckResult | null {
  if (!userId) return null;

  const stale = readEntitlementsCacheStale();
  if (stale && stale.userId === userId && stale.isPaid) {
    console.warn('[Entitlements] API error — using stale cache for paid user');
    return {
      isPaid: stale.isPaid,
      isActive: stale.isActive,
      isTrialing: stale.isTrialing,
      planName: stale.planName,
      canAccessPortfolios: stale.canAccessPortfolios || false,
      canAccessRadar: stale.canAccessRadar || false,
    };
  }

  const premiumEver = readPremiumEverFlag();
  if (premiumEver && premiumEver.userId === userId && premiumEver.wasPremium) {
    console.warn('[Entitlements] API error — using premium_ever flag for paid user');
    return {
      isPaid: true,
      isActive: true,
      isTrialing: false,
      canAccessPortfolios: stale?.canAccessPortfolios ?? true,
      canAccessRadar: stale?.canAccessRadar ?? true,
    };
  }

  return null;
}

/**
 * Проверяет статус подписки с кэшированием и защитой от дублирующих запросов
 * @param userId - ID пользователя (для валидации кэша)
 * @param forceRefresh - принудительно обновить кэш
 * @returns Promise с результатом проверки
 */
export async function getEntitlements(
  userId: string | null,
  forceRefresh = false
): Promise<PaywallCheckResult> {
  // Проверяем, не идет ли уже проверка на другой вкладке
  if (!forceRefresh && typeof window !== 'undefined') {
    try {
      const lastCheck = localStorage.getItem(GLOBAL_ENTITLEMENTS_CHECK_KEY);
      if (lastCheck) {
        const age = Date.now() - parseInt(lastCheck, 10);
        if (age < GLOBAL_ENTITLEMENTS_CHECK_TTL) {
          // Недавно была проверка, используем кэш
          const cached = readEntitlementsCache();
          if (cached && cached.userId === userId) {
            return {
              isPaid: cached.isPaid,
              isActive: cached.isActive,
              isTrialing: cached.isTrialing,
              planName: cached.planName,
              canAccessPortfolios: cached.canAccessPortfolios || false,
              canAccessRadar: cached.canAccessRadar || false,
            };
          }
        }
      }
    } catch (e) {
      // Игнорируем ошибки
    }
  }

  // Если есть активный запрос для того же пользователя, используем его
  if (inflightRequest && 
      inflightRequestUserId === userId &&
      Date.now() - inflightRequestTimestamp < INFLIGHT_TTL) {
    return inflightRequest;
  }
  
  // Проверяем кэш (если не принудительное обновление)
  if (!forceRefresh) {
    const cached = readEntitlementsCache();
    if (cached) {
      // Валидируем, что кэш для того же пользователя
      if (cached.userId === userId) {
        // Возвращаем кэшированные данные, но запускаем обновление в фоне
        const cachedResult: PaywallCheckResult = {
          isPaid: cached.isPaid,
          isActive: cached.isActive,
          isTrialing: cached.isTrialing,
          planName: cached.planName,
          canAccessPortfolios: cached.canAccessPortfolios || false,
          canAccessRadar: cached.canAccessRadar || false,
        };
        
        // Запускаем обновление в фоне (не блокируем)
        refreshEntitlementsInBackground(userId);
        
        return cachedResult;
      }
    }
  }
  
  // Помечаем начало проверки
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(GLOBAL_ENTITLEMENTS_CHECK_KEY, Date.now().toString());
    } catch (e) {
      // Игнорируем ошибки
    }
  }
  
  // Создаем новый запрос
  const requestPromise = fetchEntitlementsFromAPI();
  inflightRequest = requestPromise;
  inflightRequestTimestamp = Date.now();
  inflightRequestUserId = userId;
  
  try {
    const result = await requestPromise;

    // Если API вернул ошибку — НЕ доверяем `isPaid: false`. Пробуем восстановить
    // последнее известное состояние из stale-кэша или premium_ever флага,
    // чтобы не показать ложный пейвол премиум-юзеру при сетевых сбоях / медленном Stripe.
    if (result._isError) {
      const fallback = resolvePaidFallback(userId);
      if (fallback) return fallback;
      // Никаких следов о paid-статусе нет — возвращаем как есть (free).
      const { _isError, ...cleanResult } = result;
      return cleanResult;
    }

    // Сохраняем в кэш только при успешном ответе
    const { _isError, ...cleanResult } = result;
    writeEntitlementsCache(userId, cleanResult);
    return cleanResult;
  } catch (error) {
    console.error('Error fetching entitlements:', error);

    // Сначала пробуем валидный (свежий) кэш
    const cached = readEntitlementsCache();
    if (cached && cached.userId === userId) {
      return {
        isPaid: cached.isPaid,
        isActive: cached.isActive,
        isTrialing: cached.isTrialing,
        planName: cached.planName,
        canAccessPortfolios: cached.canAccessPortfolios || false,
        canAccessRadar: cached.canAccessRadar || false,
      };
    }

    // Затем stale-кэш + premium_ever как защита от мерцания пейвола
    const fallback = resolvePaidFallback(userId);
    if (fallback) return fallback;

    // Если ни одного следа paid-статуса нет — выбрасываем, чтобы caller
    // мог решить (показать unknown / повторить позже)
    throw new Error('Entitlements check failed and no cache available');
  } finally {
    // Очищаем inflight request через небольшую задержку
    setTimeout(() => {
      if (inflightRequest === requestPromise) {
        inflightRequest = null;
        inflightRequestUserId = null;
      }
    }, 100);
  }
}

/**
 * Обновляет entitlements в фоне (для SWR паттерна)
 */
async function refreshEntitlementsInBackground(userId: string | null): Promise<void> {
  try {
    const result = await fetchEntitlementsFromAPI();
    
    // Записываем в кэш ТОЛЬКО если это не ошибка
    if (!result._isError) {
      // Убираем флаг _isError перед записью в кэш
      const { _isError, ...cleanResult } = result;
      writeEntitlementsCache(userId, cleanResult);
      
      // Отправляем обновление через BroadcastChannel
      if (entitlementsChannel) {
      entitlementsChannel.postMessage({
        type: 'ENTITLEMENTS_UPDATE',
        cache: {
          userId,
          isPaid: cleanResult.isPaid,
          isActive: cleanResult.isActive,
          isTrialing: cleanResult.isTrialing,
          planName: cleanResult.planName,
          canAccessPortfolios: cleanResult.canAccessPortfolios || false,
          canAccessRadar: cleanResult.canAccessRadar || false,
          timestamp: Date.now(),
        },
        timestamp: Date.now(),
      });
      }
    } else {
      // При ошибке НЕ записываем в кэш и НЕ отправляем broadcast
      console.warn('Background entitlements refresh failed (error response)');
    }
  } catch (error) {
    // Игнорируем ошибки фонового обновления
    // НЕ записываем в кэш при ошибке - это не должно разлетаться по вкладкам
    console.warn('Background entitlements refresh failed:', error);
  }
}

/**
 * Выполняет запрос к API для получения статуса подписки
 */
async function fetchEntitlementsFromAPI(): Promise<PaywallCheckResult & { _isError?: boolean }> {
  // 12 секунд: /api/subscriptions/status под капотом синхронно ходит в Stripe несколько раз
  // (subscription.retrieve + paymentMethods.retrieve + invoice preview), и при медленных
  // ответах Stripe легко перевалит за 5с. С таймаутом меньшим, чем продолжительность ответа,
  // только что оплативший пользователь видел бы UI как "Free" вместо реального статуса.
  const REQUEST_TIMEOUT = 12000;
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    
    try {
      const response = await fetch('/api/subscriptions/status', {
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        return {
          isPaid: false,
          isActive: false,
          isTrialing: false,
          canAccessPortfolios: false,
          canAccessRadar: false,
          _isError: true,
        };
      }
      
      const data = await response.json();
      const isPaid = data.isActive || data.isTrialing;
      
      return {
        isPaid,
        isActive: data.isActive || false,
        isTrialing: data.isTrialing || false,
        planName: data.plan?.name,
        canAccessPortfolios: data.canAccessPortfolios || false,
        canAccessRadar: data.canAccessRadar || false,
        _isError: false,
      };
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError' || fetchError.message?.includes('aborted')) {
        console.warn('Entitlements check timed out');
        return {
          isPaid: false,
          isActive: false,
          isTrialing: false,
          canAccessPortfolios: false,
          canAccessRadar: false,
          _isError: true,
        };
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error fetching entitlements from API:', error);
    return {
      isPaid: false,
      isActive: false,
      isTrialing: false,
      canAccessPortfolios: false,
      canAccessRadar: false,
      _isError: true,
    };
  }
}

/**
 * Получает статус entitlements для UI (unknown, free, paid)
 */
export function getEntitlementsStatus(
  userId: string | null,
  cached: EntitlementsCache | null
): EntitlementsStatus {
  if (!userId) {
    return 'free';
  }
  
  if (!cached || cached.userId !== userId) {
    return 'unknown';
  }
  
  return cached.isPaid ? 'paid' : 'free';
}

/**
 * Создает обработчик для BroadcastChannel сообщений
 * @param onUpdate - callback при обновлении entitlements
 */
export function setupEntitlementsBroadcastListener(
  onUpdate: (cache: EntitlementsCache) => void
): () => void {
  if (!entitlementsChannel) return () => {};
  
  const handleMessage = (event: MessageEvent) => {
    if (event.data?.type === 'ENTITLEMENTS_UPDATE') {
      const { cache } = event.data;
      if (cache) {
        // Обновляем localStorage
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(ENTITLEMENTS_CACHE_KEY, JSON.stringify(cache));
            localStorage.setItem(ENTITLEMENTS_CACHE_TIMESTAMP_KEY, cache.timestamp.toString());
          } catch (e) {
            console.warn('Error updating entitlements cache from broadcast:', e);
          }
        }
        
        // Вызываем callback
        onUpdate(cache);
      }
    } else if (event.data?.type === 'ENTITLEMENTS_CLEAR') {
      // Очищаем кэш
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem(ENTITLEMENTS_CACHE_KEY);
          localStorage.removeItem(ENTITLEMENTS_CACHE_TIMESTAMP_KEY);
        } catch (e) {
          console.warn('Error clearing entitlements cache from broadcast:', e);
        }
      }
      
      // Вызываем callback с null
      onUpdate({
        userId: null,
        isPaid: false,
        isActive: false,
        isTrialing: false,
        canAccessPortfolios: false,
        canAccessRadar: false,
        timestamp: Date.now(),
      });
    }
  };
  
  entitlementsChannel.addEventListener('message', handleMessage);
  
  return () => {
    entitlementsChannel?.removeEventListener('message', handleMessage);
  };
}

/**
 * Удобные функции для проверки подписки (с кэшированием)
 */
export async function canUsePaywallFeatureCached(userId: string | null): Promise<boolean> {
  const result = await getEntitlements(userId);
  return result.isPaid;
}

export async function hasActiveSubscriptionCached(userId: string | null): Promise<boolean> {
  const result = await getEntitlements(userId);
  return result.isActive && !result.isTrialing;
}

